/*****************************************************************************
 * ClusterMap Module
 ****************************************************************************/

//Internal modules, these are bundled into clustermap.js
import { figue } from 'figue';
import { jLouvain } from 'jLouvain';
import '../styles/clustermap.less';
import d3wl from 'utils/d3-wl-util';

// External modules, these have to be embedded separately
import _ from 'lodash';
import BubbleLegend from 'modules/bubblelegend/BubbleLegend';
import getDummyData from 'utils/getDummyData';
import i18n from 'localize';
import * as d3 from 'd3c';

//main chart function
export const ClusterMap = function(uc) {
	// default private variables
	var _this = this;
	var _wrapper = 'body';
	var _width = null;
	var _height = null;
	var _clustersCount = 5;
	var _clustering = 'kmeans'; // supports ['louvain', 'kmeans', 'hierarchical']
	var _data;
	var _isES1Data = false;
	var _keywordFilter; // keyword string we'd like to filter from the data set, e.g. current search term
	var _debug = false;
	var _labels = true;
	var _forceX = 0.2;
	var _forceY = 0.08;
	var _currScale = 1;
	var _customForceX = false;
	var _customForceY = false;
	var _dragBoundaries = [];
	var _showBubbleLegend = false;
	var _fontSizeAmplifier = 1.0;

	var updateColor = uc;
	var updateDuration = 500;
	var updating = true;

	var benchmark;
	var steps = 0;

	// initialize static variables
	var minValue = 0; // minimum data value
	var maxValue = 1; // maximum data value
	var minR = 2.5; // minimum node radius
	var maxR = 8; // maximum node radius
	var nodePadding = 2; // padding between nodes
	var clusterPadding = maxR * 3; // padding between clusters
	var clusterBorder = 1;

	var legend = new BubbleLegend('clusterMapLegend');

	// scale for node sizes
	var size = d3
		.scaleSqrt()
		.domain([minValue, maxValue])
		.range([minR, maxR]);

	var hideLabels = function() {
		d3
			.selectAll('.clusterMapClusterLabels, .clusterMapClusterLabelStrokes')
			//.style('display', 'none')
			.style('opacity', 0);
	};

	var showLabels = function() {
		d3
			.selectAll('.clusterMapClusterLabels, .clusterMapClusterLabelStrokes')
			.transition()
			.style('display', 'block')
			.style('opacity', 1);
	};

	var draggedCluster = null;

	var checkBoundaries = (min, max, v) => {
		if (v < min) {
			return min;
		} else if (v > max) {
			return max;
		} else {
			return v;
		}
	};

	//To be able to adapt better to changing width and height parameters this interpolator functions are provided
	//Functions are based on width/height combinations with corresponding parameters that proved useful from experience
	//For forceX and forceY the coefficients are based on 338x110, 338x206 and 338x638
	//nodePadding is based on 338x110 and 338x206
	//For the other interpolators the coefficients are based on 338x206 and 928x638
	//The corresponding parameters are listed below for each interpolator.
	var interpolator = {
		//old calculation was based on maxR = _width / 50
		maxRadius: () =>
			checkBoundaries(
				2.5,
				24,
				-75.70204298483068 + Math.log(_width * _height) * 7.091973428655596
			),
		//quadratic function through three points: (338x206,10.50),(345x300,10.95),(928x638,20.1)
		//fits atm for dashbooard, storypact and noaa
		maxGroups: () =>
			checkBoundaries(
				1,
				30,
				-1.501095611 * Math.pow(10, -11) * Math.pow(_width * _height, 2) +
				0.0000291694356096 * _width * _height +
				8.091764529
			),
		//_nodeSize === 'normal' ? 14 : 8;
		fontSize: () =>
			checkBoundaries(
				7,
				20,
				-23.257724499933076 + Math.log(_width * _height) * 2.803151552828299
			),
		//was generally 2, appeared too big for small windows like 338x110
		nodePadding: () =>
			checkBoundaries(
				0.1,
				2,
				-22.882683013504607 + Math.log(_width * _height) * 2.2314462310904655
			),
		//18,56 * 3 for 928x638 (old maxRadius) and 17 for 338x206 (was 6,76 * 3 before)
		clusterPadding: () =>
			checkBoundaries(
				10,
				72,
				-184.508130609569 + Math.log(_width * _height) * 18.0709836772331
			),
		//cluster stroke depends on radius, otherwise cluster overlay appears to small or too big
		clusterStrokeFactor: r =>
			checkBoundaries(
				2.5,
				4.5,
				4.37206369518828 + Math.log(r) * -0.46972255931233
			),
		forceX: () =>
			checkBoundaries(
				0.03,
				0.3,
				0.2631979 - 0.3808805 * Math.exp(-1.095253 * _width / _height)
			),
		forceY: () =>
			checkBoundaries(
				0.03,
				0.3,
				0.04821204 + 0.3832978 * Math.exp(-1.748448 * _width / _height)
			)
	};

	const fitToWrapperBoundaries = function() {
		const dim = d3
			.select(_wrapper)
			.node()
			.getBoundingClientRect();

		_width = dim.width;
		_height = dim.height;
		if (!_customForceX) {
			_forceX = interpolator.forceX();
		}
		if (!_customForceY) {
			_forceY = interpolator.forceY();
		}
	};

	// force layout
	var forceFactory = function(nodes) {
		hideLabels();

		if (_debug) {
			var ms = new Date() - benchmark;
			var fps = steps / (ms / 1000);
			/* eslint-disable no-console */
			console.info('Layout done in %i milliseconds', ms);
			console.info('Steps: %i', steps);
			console.info('FPS: %i', fps);
			/* eslint-enable no-console */
		}

		var forceInstance = d3
			.forceSimulation(nodes)
			//.force('charge', d3.forceManyBody().strength(20))
			.force('cluster', forceCluster())
			.force('Y', d3.forceY().strength(_forceX))
			.force('X', d3.forceX().strength(_forceY))
			//.force('XSpread', forceXSpread())
			//.force("center", d3.forceCenter())
			//.force('collide', d3.forceCollide().radius(d => d.radius).iterations(200).strength(1))
			.force('collide', forceCollisions().iterations(50))
			.on('end', showLabels);

		var clusterCenters = d3
			.nest()
			.key(d => d.cluster)
			.rollup(leaves => _.max(leaves, 'radius'))
			.entries(nodes);

		clusterCenters = _.map(clusterCenters, d => d.value);

		function forceCluster() {
			var nodes,
				strength = 0.1;

			function force(alpha) {
				nodes.forEach(function(d) {
					var factor = alpha;
					if (draggedCluster && d.cluster === draggedCluster) {
						factor = 20 * alpha < 0.7 ? 20 * alpha : 0.7;
					}

					var clusterCenter = _.find(clusterCenters, {
						cluster: d.cluster
					});
					if (!clusterCenter || clusterCenter.id === d.id) {
						return;
					}

					let x = d.x - clusterCenter.x,
						y = d.y - clusterCenter.y,
						l = Math.sqrt(x * x + y * y),
						r = d.radius + clusterCenter.radius;

					if (l !== r) {
						l = (l - r) / l * factor;
						x *= l;
						y *= l;
						d.x -= x;
						d.y -= y;
						clusterCenter.x += x;
						clusterCenter.y += y;
					}
				});
			}

			force.initialize = function(_) {
				nodes = _;
			};

			force.strength = _ => {
				strength = _ == null ? strength : _;
				return force;
			};

			return force;
		}

		//spread nodes in an oval form over X Axis, worked better in d3v3 version
		function forceXSpread() {
			var nodes, firstBound, secondBound, thirdBound, fourthBound, fifthBound;

			function force(alpha) {
				var clusterMapTranslate = d3.select('.clusterMapTranslate');
				firstBound = 0;
				var total_width = _width;
				if (clusterMapTranslate.size() > 0) {
					var transform = d3wl.transform(clusterMapTranslate.attr('transform'));
					firstBound = transform.translateX * -1;
					total_width = _width / transform.scaleX;
				}

				secondBound = firstBound + total_width / 5;
				thirdBound = secondBound + total_width / 5;
				fourthBound = thirdBound + total_width / 5;
				fifthBound = fourthBound + total_width / 5;

				var a = 0;
				var b = 0;
				var c = 0;
				var de = 0;
				var e = 0;
				nodes.forEach(function(d) {
					if (d.x < secondBound) {
						d.y *= 1 - 0.5 * alpha;
						a++;
					}
					if (d.x > secondBound && d.x < thirdBound) {
						d.y *= 1 - 0.3 * alpha;
						b++;
					}
					if (d.x > thirdBound && d.x < fourthBound) {
						d.y *= 1 - 0.1 * alpha;
						c++;
					}
					if (d.x > fourthBound && d.x < fifthBound) {
						d.y *= 1 - 0.3 * alpha;
						de++;
					}
					if (d.x > fifthBound) {
						d.y *= 1 - 0.5 * alpha;
						e++;
					}
				});
				//console.log("distribution: " + a+ " " + b + " " + c + " " + de + " " + e)
			}

			force.initialize = function(_) {
				nodes = _;
			};

			return force;
		}

		// detect collisions between nodes
		function forceCollisions() {
			var nodes,
				iterations = 0.1;

			function force(alpha) {
				alpha = 0.5;
				var quadtree = d3
					.quadtree()
					.x(a => a.x)
					.y(b => b.y)
					.addAll(nodes);

				for (var k = 0; k < iterations; ++k) {
					nodes.forEach(function(d) {
						var r = d.radius + maxR + Math.max(nodePadding, clusterPadding),
							nx1 = d.x - r,
							nx2 = d.x + r,
							ny1 = d.y - r,
							ny2 = d.y + r;

						quadtree.visit(function(quad, x1, y1, x2, y2) {
							if (quad.data && quad.data !== d) {
								var qpRadius = quad.data.radius;
								var x = d.x - quad.data.x,
									y = d.y - quad.data.y,
									l = Math.sqrt(x * x + y * y),
									r =
										d.radius +
										qpRadius +
										(d.cluster === quad.data.cluster
											? nodePadding
											: clusterPadding);

								if (l < r) {
									l = (l - r) / l * alpha;
									x *= l;
									y *= l;
									d.x -= x;
									d.y -= y;
									quad.data.x += x;
									quad.data.y += y;
								}
							}

							return x1 > nx2 || x2 < nx1 || y1 > ny2 || y2 < ny1;
						});
					});
				}
			}

			force.iterations = _ => {
				iterations = _ == null ? iterations : _;
				return force;
			};

			force.initialize = function(_) {
				nodes = _;
			};

			return force;
		}

		var line = d3
			.line()
			.x(d => d[0])
			.y(d => d[1])
			.curve(d3.curveLinearClosed);

		var clusterNodes = {};
		_.each(nodes, function(n) {
			if (typeof clusterNodes[n.cluster] === 'undefined') {
				clusterNodes[n.cluster] = [];
			}
			clusterNodes[n.cluster].push(n);
		});

		// update the graph
		forceInstance.on('tick', function(e) {
			steps++;
			d3
				.selectAll('.clusterMapNodes, .clusterMapNodeBackgrounds')
				.selectAll('circle')
				.attr('cx', d => d.x)
				.attr('cy', d => d.y);

			var hullPath = function(d) {
				var filteredNodes = clusterNodes[d.key];

				filteredNodes = _.map(filteredNodes, f => [f.x, f.y]);
				filteredNodes = _.uniq(filteredNodes, f => f[0] + '--' + f[1]);

				if (filteredNodes.length < 3) {
					return '';
				}

				var hull = d3.polygonHull(filteredNodes);
				return line(hull);
			};

			d3
				.select(_wrapper)
				.selectAll('.clusterMapClusters')
				.selectAll('g.clusterMapCluster')
				.each(function(d) {
					d3
						.select(this)
						.select('.clusterMapClusterInnerPath')
						.attr('stroke-width', d => d.clusterMaxRadius)
						.attr('d', hullPath(d));
				});

			d3
				.select(_wrapper)
				.selectAll('.clusterMapClusterOverlays')
				.selectAll('g.clusterMapClusterOverlay')
				.each(function(d) {
					d3
						.select(this)
						.select('.clusterMapClusterInnerPathOverlay')
						.attr('stroke-width', d => d.clusterMaxRadius - clusterBorder)
						.attr('d', hullPath(d));
				});

			// TODO: Resolve reference to portal environment
			if (
				_labels &&
				(
					!d3.select('.SemanticMap').node() ||
					!d3.select('.SemanticMap').classed('closed')
				)
			) {
				d3
					.selectAll('.clusterMapClusterLabels, .clusterMapClusterLabelStrokes')
					.selectAll('text')
					.attr('x', function(d) {
						var filteredNodes = clusterNodes[d.key];
						return filteredNodes
							? d3.sum(_.map(filteredNodes, 'x')) / filteredNodes.length
							: 0;
					})
					.attr('y', function(d) {
						var filteredNodes = clusterNodes[d.key];
						return filteredNodes
							? d3.sum(_.map(filteredNodes, 'y')) / filteredNodes.length
							: 0;
					});
			}

			if (size.domain()[0] === size.domain()[1] && nodes.length === 1) {
				updateLegend(_currScale); // update the legend if only one bubble exists
			} else {
				autozoom(); // otherwise do autozoom
			}

			if (forceInstance.alpha() < 0.04) {
				forceInstance.stop();
				showLabels();
				_this.observer.notify('renderingFinished');
			}
		});

		return forceInstance;
	};

	// update the legend
	function updateLegend(scale) {
		if (legend.initialized && _width !== 0 && _height !== 0) {
			legend.update(_width, _height, size, scale);
		}
	}

	var force = forceFactory([]);

	function autozoom() {
		var att = (x, y, k) => `translate(${x},${y})scale(${k})`;

		var clustermaptranslate = d3.select('.clusterMapTranslate');

		clustermaptranslate.attr('transform', att(0, 0, 1));

		var node = clustermaptranslate.node();

		if (node) {
			var currW = node.getBoundingClientRect().width;
			var currH = node.getBoundingClientRect().height;

			if (currW === 0 && currH === 0) {
				return;
			}

			var currX = node.getBBox().x;
			var currY = node.getBBox().y;

			var fontSize = interpolator.fontSize() * _fontSizeAmplifier;

			var factorX = _width / currW;
			var factorY = _height / currH;

			var k,
				centerX,
				centerY = null;

			k = Math.min(factorX, factorY, 4);
			if (k < 4) {
				centerX = factorX < factorY ? 0 : (_width - currW * k) / 2;
				centerY = factorX > factorY ? 0 : (_height - currH * k) / 2;
			} else {
				centerX = (_width - currW * k) / 2;
				centerY = (_height - currH * k) / 2;
			}

			var x = centerX + -currX * k;
			var y = centerY + -currY * k;

			_currScale = k; // save the current scale

			// for debugging this displays a bounding box of the clusters
			if (_debug) {
				d3
					.select('.clusterMapTranslateRect')
					.attr('transform', att(x, y, k))
					.attr('width', currW)
					.attr('height', currH)
					.attr('x', currX)
					.attr('y', currY);
			}

			clustermaptranslate.attr('transform', att(x, y, k));

			// scale text
			if (_labels) {
				clustermaptranslate
					.selectAll('text.cLabelStroke, text.cLabel')
					.style('font-size', fontSize / k + 'px');
			}

			updateLegend(k);
		}

		return;
	}

	var colorMode = 'sentiment';
	var colorScale = d3
		.scaleLinear()
		.domain([-1, 0, 1])
		.range(['#af3232', '#808080', '#3caf50']); // @sentiment-color-negative, @sentiment-color-neutral, @sentiment-color-positive

	var filterAnnotations = function(annotations) {
		if (_keywordFilter === false || typeof _keywordFilter === 'undefined') {
			return annotations;
		}

		let stripFilter = filter => filter.toLowerCase().replace(/"/g, '');
		var strippedFilter = null;

		if (Array.isArray(_keywordFilter)) {
			strippedFilter = _keywordFilter.map(filter => stripFilter(filter));
			return annotations.filter(
				ann => !strippedFilter.find(filter => filter === ann.name)
			);
		} else {
			strippedFilter = stripFilter(_keywordFilter);
			return annotations.filter(ann => strippedFilter !== ann.name);
		}
	};

	var highlightNodes = function(ids) {
		d3
			.select(_wrapper)
			.select('.clusterMapNodes')
			.selectAll('circle')
			.classed('highlighted', function(d) {
				if (typeof ids === 'undefined') {
					return false;
				}
				return ids && ids.includes(d.id);
			});
	};

	var updateClusters = function(clusters) {
		var wrap = d3.select(_wrapper);

		wrap
			.selectAll('path.clusterMapClusterInnerPath, path.clusterMapClusterInnerPathOverlay')
			.classed('highlight', d => clusters && clusters.includes(d.key));

		// wrap
		// 	.select('.clusterMapClusters')
		// 	.selectAll('path.clusterMapClusterInnerPath')
		// 	.style('opacity', function(d) {
		// 		if (!Array.isArray(clusters)) {
		// 			return null;
		// 		}
		//		return clusters && clusters.includes(d.key) ? null : 0.5;
		// 	});

		if (!clusters) {
			wrap
				.select('.clusterMapNodes')
				.selectAll('circle')
				.style('opacity', null);

			if (_labels) {
				wrap
					.selectAll('.clusterMapClusterLabels, .clusterMapClusterLabelStrokes')
					.selectAll('text')
					.style('opacity', 1);
			}
		} else {
			// wrap
			// 	.select('.clusterMapNodes')
			// 	.selectAll('circle')
			// 	.style('opacity', function(d) {
			// 		return clusters && clusters.includes(d.cluster) ? 1 : 0.1;
			// 	});

			if (_labels) {
				d3
					.selectAll('.clusterMapClusterLabels, .clusterMapClusterLabelStrokes')
					.selectAll('text')
					.style('opacity', d => clusters && clusters.includes(d.key) ? 0 : 1);
			}
		}
	};

	function makeDataES1Compatible(data) {
		data.documents.forEach(x => {
			if (!x.contentId && x.contentid) {
				x.contentId = x.contentid;
			}
		});
		data.documents = _.uniq(data.documents, 'contentId');
		data.documents.forEach(x => {
			if (!x.clusterFeatures) {
				if (x.documentKeywords) {
					x.clusterFeatures = x.documentKeywords.map(y => ({
						name: y.keyword
					}));
				} else if (
					x.keywords &&
					x.keywords.length !== 0 &&
					x.keywords[0].name
				) {
					x.clusterFeatures = x.keywords.map(y => ({ name: y.name }));
				} else if (
					x.keywords &&
					x.keywords.length !== 0 &&
					x.keywords[0].keyword
				) {
					x.clusterFeatures = x.keywords.map(y => ({ name: y.keyword }));
				}
			}

			if (!x.reach) {
				x.reach = 1;
			}
			if (!x.sentiment) {
				x.sentiment = 0;
			}
			if (!x.name) {
				x.name = 'not defined';
			}
			if (!x.sourceId) {
				x.sourceId = undefined;
			}
		});

		return data;
	}

	var chart = function(selection) {
		_this.observer.notify('renderingStarted');

		selection.each(function(data) {
			var groupsData = data.groups;
			var nodes = _.reduce(
				groupsData,
				function(flatNodes, group) {
					_.each(group.values, function(v) {
						flatNodes.push(v);
					});
					return flatNodes;
				},
				[]
			);

			force.stop();

			// initialize the SVG canvas
			// select the svg element, if it exists
			var wrap = d3.select(_wrapper);
			var svg = wrap.select('.clustermap');

			if (!groupsData || (groupsData && groupsData.length === 0)) {
				groupsData = getDummyData('clustermapGroups');
				nodes = getDummyData('clustermapNodes');

				let minv = 1;
				let maxv = 0;
				nodes.forEach(function(n) {
					if (n.reach < minv) {
						minv = n.reach;
					}
					if (n.reach > maxv) {
						maxv = n.reach;
					}
				});

				size.domain([minv, maxv]);

				nodes.forEach(n =>
					Object.assign(n, {
						radius: size(n.reach),
						originalRadius: size(n.reach)
					})
				);

				groupsData.forEach(g => {
					var maxr = Math.max(
						...nodes.filter(x => x.cluster === g.key).map(x => x.radius)
					);
					g.clusterMaxRadius = interpolator.clusterStrokeFactor(maxr) * maxr;
				});
			}

			// create the skeletal chart
			if (svg.empty()) {
				svg = wrap
					.append('svg:svg')
					.classed('clustermap', true)
					.on('mouseleave', function() {
						updateClusters();
						_this.observer.notify('mouseout', d3);
					});

				var translate = svg
					.append('g')
					.classed('clusterMapTranslate', true);

				// defs section for masks
				svg.append('defs');

				// cluster shapes
				translate
					.append('g')
					.classed('clusterMapClusters', true);

				// cluster shape overlays
				translate
					.append('g')
					.classed('clusterMapClusterOverlays', true);

				// document node backgrounds
				translate
					.append('g')
					.classed('clusterMapNodeBackgrounds', true);

				// document nodes
				translate
					.append('g')
					.classed('clusterMapNodes', true);

				if (_labels) {
					// cluster label strokes
					translate
						.append('g')
						.classed('clusterMapClusterLabelStrokes', true);

					// cluster labels
					translate
						.append('g')
						.classed('clusterMapClusterLabels', true);
				}

				// for debugging this displays a bounding box of the clusters
				if (_debug) {
					svg
						.append('rect')
						.attr('stroke', 'blue')
						.attr('fill', 'none')
						.classed('clusterMapTranslateRect', true);
				}
			}

			force = forceFactory(nodes); //starting automatically

			var getDragBoundaries = () => {
				var boundaries = [9999, -9999, 9999, -9999];
				let nodes = d3.selectAll('.clusterMapNodes circle').nodes();

				nodes.forEach(node => {
					let x = node.cx.animVal.value;
					let y = node.cy.animVal.value;
					if (x < boundaries[0]) boundaries[0] = x;
					if (x > boundaries[1]) boundaries[1] = x;
					if (y < boundaries[2]) boundaries[2] = y;
					if (y > boundaries[3]) boundaries[3] = y;
				});

				return boundaries;
			};

			//define drag methods for force layout
			function dragstarted() {
				hideLabels();
				_dragBoundaries = getDragBoundaries();
				_this.observer.notify('renderingStarted');
				draggedCluster = d3.event.subject.cluster;
				if (!d3.event.active) force.alphaTarget(0.3).restart();
				d3.event.subject.fx = d3.event.subject.x;
				d3.event.subject.fy = d3.event.subject.y;
			}

			function dragged() {
				var d = d3.event;
				if (d.x > _dragBoundaries[0] && d.x < _dragBoundaries[1])
					d3.event.subject.fx = d.x;
				if (d.y > _dragBoundaries[2] && d.y < _dragBoundaries[3])
					d3.event.subject.fy = d.y;
			}

			function dragended() {
				draggedCluster = null;
				if (!d3.event.active) force.alphaTarget(0);
				d3.event.subject.fx = null;
				d3.event.subject.fy = null;
			}

			benchmark = new Date();
			steps = 0;
			//force.size([_width, _height]).start();
			//instead if needed
			//force.x =
			//force.y =
			//force.start()

			svg
				.attr('width', _width)
				.attr('height', _height);

			var clusterGs = wrap
				.select('.clusterMapClusters')
				.selectAll('g.clusterMapCluster')
				.data(groupsData, d => d.key);

			var g = clusterGs
				.enter()
				.append('g')
				.attr('transform', 'translate(-10,-10)')
				.classed('clusterMapCluster', true)
				.each(function(d) {
					d3
						.select(this)
						.classed('clusterMapCluster' + d.key, true);
				});

			g
				.append('path')
				.attr('transform', 'translate(10,10)')
				.attr('stroke-width', d => d.clusterMaxRadius)
				.classed('clusterMapClusterInnerPath', true)
				.each(function(d) {
					d3
						.select(this)
						.classed('clusterMapClusterInnerPath' + d.key, true);
				});

			clusterGs
				.exit()
				.remove();

			var clusterGOverlays = wrap
				.select('.clusterMapClusterOverlays')
				.selectAll('g.clusterMapClusterOverlay')
				.data(groupsData, d => d.key);

			var gOverlay = clusterGOverlays
				.enter()
				.append('g')
				.attr('transform', 'translate(-10,-10)')
				.classed('clusterMapClusterOverlay', true)
				.each(function(d) {
					d3
						.select(this)
						.classed('clusterMapClusterOverlay' + d.key, true);
				});

			gOverlay
				.append('path')
				.attr('stroke-width', d => d.clusterMaxRadius - clusterBorder)
				.attr('transform', 'translate(10,10)')
				.classed('clusterMapClusterInnerPathOverlay', true)
				.each(function(d) {
					d3
						.select(this)
						.classed('clusterMapClusterInnerPathOverlay' + d.key, true);
				});

			clusterGOverlays
				.exit()
				.remove();

			// select all node backgrounds
			var clusterNodeBackgrounds = d3
				.select('.clusterMapNodeBackgrounds')
				.selectAll('circle');

			clusterNodeBackgrounds
				.data(nodes, d => d.id + '_' + d.cluster);

			// remove old node backgrounds
			clusterNodeBackgrounds
				.exit()
				.remove();

			// add new node backgrounds
			clusterNodeBackgrounds = clusterNodeBackgrounds
				.enter()
				.append('circle')
				.attr('r', function(d) {
					d.radius = 0;
					return 0;
				})
				.merge(clusterNodeBackgrounds) // update node backgrounds
				.transition()
				.duration(750)
				.delay((d, i) => i * 5 * d.reach)
				.attrTween('r', function(d) {
					var i = d3.interpolate(d.radius, d.originalRadius);
					return function(t) {
						d.radius = i(t);
						return d.radius;
					};
				});

			// select all nodes
			var clusterNodes = d3
				.select('.clusterMapNodes')
				.selectAll('circle')
				.interrupt('clusterNodesGreyOut') //if grey out animation finishes after update selection some nodes stay grey
				.data(nodes, d => d.id + '_' + d.cluster);

			// remove old nodes
			clusterNodes
				.exit()
				.remove();

			// add new nodes
			clusterNodes = clusterNodes
				.enter()
				.append('circle')
				.attr('r', function(d) {
					d.radius = 0;
					return 0;
				})
				.call(
					d3
						.drag()
						.on('start', dragstarted)
						.on('drag', dragged)
						.on('end', dragended)
				)
				.merge(clusterNodes) // update nodes
				.attr('fill', d => updating ? d.updateColor : d.color)
				.attr('stroke', d => updating ? d.updateColor : d.color);

			clusterNodes
				.transition()
				.duration(750)
				.delay((d, i) => i * 5 * d.reach)
				.attrTween('r', function(d) {
					var i = d3.interpolate(d.radius, d.originalRadius);
					return function(t) {
						d.radius = i(t);
						return d.radius;
					};
				});

			if (_labels) {
				var cLabelStrokes = wrap
					.select('.clusterMapClusterLabelStrokes')
					.selectAll('.clusterMapLabelStroke')
					.data(groupsData, d => d.key);

				var cLabelStrokesEnter = cLabelStrokes
					.enter()
					.append('g')
					.attr('class', 'clusterMapLabelStroke');

				cLabelStrokesEnter
					.append('text')
					.attr('class', 'cLabelStroke clusterMapCLS3')
					.attr('dy', '-0.65em');
				cLabelStrokesEnter
					.append('text')
					.attr('class', 'cLabelStroke clusterMapCLS2')
					.attr('dy', '0.35em');
				cLabelStrokesEnter
					.append('text')
					.attr('class', 'cLabelStroke clusterMapCLS1')
					.attr('dy', '1.35em');

				cLabelStrokes
					.exit()
					.remove();

				cLabelStrokes = cLabelStrokesEnter.merge(cLabelStrokes); //merge ENTER + UPDATE selections

				cLabelStrokes
					.select('.clusterMapCLS3')
					.text(d => d.keywords[0] !== undefined ? d.keywords[0].name : '');
				cLabelStrokes
					.select('.clusterMapCLS2')
					.text(d => d.keywords[1] !== undefined ? d.keywords[1].name : '');
				cLabelStrokes
					.select('.clusterMapCLS1')
					.text(d => d.keywords[2] !== undefined ? d.keywords[2].name : '');

				var cLabels = wrap
					.select('.clusterMapClusterLabels')
					.selectAll('.clusterMapLabel')
					.data(groupsData, d => d.key);

				var cLabelsEnter = cLabels
					.enter()
					.append('g')
					.attr('class', 'clusterMapLabel');

				cLabelsEnter
					.append('text')
					.attr('class', 'cLabel clusterMapCL3')
					.attr('dy', '-0.65em');
				cLabelsEnter
					.append('text')
					.attr('class', 'cLabel clusterMapCL2')
					.attr('dy', '0.35em');
				cLabelsEnter
					.append('text')
					.attr('class', 'cLabel clusterMapCL1')
					.attr('dy', '1.35em');

				cLabels
					.exit()
					.remove();

				cLabels = cLabelsEnter.merge(cLabels);

				cLabels
					.select('.clusterMapCL3')
					.text(d => d.keywords[0] !== undefined ? d.keywords[0].name : '');
				cLabels
					.select('.clusterMapCL2')
					.text(d => d.keywords[1] !== undefined ? d.keywords[1].name : '');
				cLabels
					.select('.clusterMapCL1')
					.text(d => d.keywords[2] !== undefined ? d.keywords[2].name : '');
			}

			//---------------------------------------------
			// interaction
			//---------------------------------------------

			// if data is available
			if (groupsData[0].key.indexOf('dummy') === -1 && updating === false) {
				svg.classed('loading', false);

				// initialize the legend
				if (!legend.initialized && _showBubbleLegend) {
					legend.initialize(_wrapper, svg, 'float', i18n.t9n('Source Reach'));
				}

				// check hover for cluster shapes
				wrap
					.selectAll('path.clusterMapClusterInnerPathOverlay')
					.on('mouseover', function() {
						if (updating === false) {
							updateClusters([d3.select(this).datum().key]);
							_this.observer.notify('mouseover', d3);
						}
					})
					.on('mouseout', function() {
						updateClusters();
						_this.observer.notify('mouseout', d3);
					});

				// check hover for node shapes
				wrap
					.select('.clusterMapNodes')
					.selectAll('circle')
					.on('mouseover', function() {
						if (updating === false) {
							updateClusters([d3.select(this).datum().cluster]);

							if (legend.initialized) {
								legend.showNode(d3.select(this).datum().reach, size);
							}

							_this.observer.notify('mouseover.bubble', d3);
						}
					})
					.on('mousemove', function() {
						if (updating === false) {
							_this.observer.notify('mousemove.bubble', d3);
						}
					})
					.on('mouseout', function() {
						updateClusters();

						if (legend.initialized) {
							legend.hideNode();
						}

						_this.observer.notify('mouseout.bubble', d3);
					})
					.on('click', function() {
						_this.observer.notify('click.bubble', d3);
					});

				// legend event listeners
				legend
					.on('highlightNodes', function(container, reach) {
						if (_wrapper === container) {
							var clusterArr = [];
							d3
								.select('.clusterMapNodes')
								.selectAll('circle')
								.classed('highlighted', function(d) {
									if (reach === d.reach) {
										clusterArr.push(d.cluster);
										return true;
									}
									return false;
								});

							updateClusters(clusterArr);
						}
					})
					.on('dehighlightNodes', function() {
						d3
							.select('.clusterMapNodes')
							.selectAll('circle')
							.classed('highlighted', false);

						updateClusters();
					});
			} else {
				svg.classed('loading', true);

				// remove the legend
				legend.remove();
			}
		});
	};

	// get the top 3 cluster terms
	function getClusterLabels(nodesSample) {
		var clusterLabels = {};
		var clusters = [];

		//add all terms to the array
		for (var i = 0; i < nodesSample.length; i++) {
			var node = nodesSample[i];
			for (var j = 0; j < node.keywords.length; j++) {
				if (clusterLabels[node.cluster]) {
					clusterLabels[node.cluster].push(node.keywords[j]);
				} else {
					clusterLabels[node.cluster] = [node.keywords[j]];
				}
				if (clusters.indexOf(node.cluster) === -1) {
					clusters.push(node.cluster);
				}
			}
		}
		clusters = _.uniq(clusters);
		clusters.sort();

		return _.map(clusters, function(d) {
			var keywords = [];
			var c = clusterLabels[d];

			for (var i = 0; i < c.length; i++) {
				var r = keywords.filter(e => c[i] === e.name);

				if (r.length === 0) {
					keywords.push({
						name: c[i],
						count: 1
					});
				} else {
					r[0].count++;
				}
			}

			keywords = _
				.sortBy(keywords, k => k.name.split(' '))
				.reverse();

			var filteredKeywords = [];

			_.each(keywords, function(keyword) {
				var addKeyword = true;
				_.each(filteredKeywords, function(checkKeyword) {
					var tokens = checkKeyword.name
						.replace('-', ' ')
						.split(' ');

					_.each(tokens, function(token) {
						if (keyword.name === token) {
							checkKeyword.count += keyword.count;
							addKeyword = false;
						}
					});
				});

				if (addKeyword) {
					filteredKeywords.push(keyword);
				}
			});

			filteredKeywords = _.sortBy(filteredKeywords, 'count').reverse();

			return {
				key: d,
				keywords: _.map(filteredKeywords.splice(0, 3), function(k) {
					var parts = k.name.split(' ');
					if (parts.length > 2) {
						k.name = parts.splice(0, 2).join(' ') + ' ...';
					}
					return k;
				})
			};
		});
	}

	//update the graph
	chart.update = function() {
		if (typeof _data === 'undefined') {
			return chart;
		}

		if (_width === null || _height === null) {
			fitToWrapperBoundaries();
		}

		var startTime = new Date();

		var clusters;
		if (_isES1Data) {
			_data = makeDataES1Compatible(_data);
		}
		var chartData = {
			clustering: _clustering,
			documents: _data.documents
		};

		switch (_clustering) {
			case 'louvain':
				clusters = chart.getLouvainClusters(_data.documents);
				break;
			case 'kmeans':
				clusters = chart.pickBestKMeansCluster(_data.documents, _clustersCount);
				break;
			case 'hierarchical':
				clusters = chart.getHierarchicalClusters(
					_data.documents,
					_clustersCount
				);
				break;
			default:
				throw new Error("invalid clustering algorithm '" + _clustering + "'");
		}

		if (typeof clusters === 'undefined') {
			d3
				.select(_wrapper)
				.datum(undefined)
				.call(chart);

			return chart;
		}

		function getAnnotations(annotations) {
			var keywords = [];

			_.each(filterAnnotations(annotations), function(annotation) {
				keywords.push(annotation.name.toLowerCase());
			});

			return keywords;
		}

		maxR = interpolator.maxRadius();
		minR = Math.max(0.8, maxR / 3);
		size.range([minR, maxR]);
		nodePadding = interpolator.nodePadding();
		clusterPadding = interpolator.clusterPadding();

		var nodes = _.map(clusters.assignments, function(d, i) {
			var doc = _data.documents[i];
			var sentimentColor = colorScale(doc.sentiment);
			var docColor = (colorMode !== 'sentiment' && doc.topic && doc.topic.color) || sentimentColor;

			// add desaturated update color to the data
			var hslColor = d3.hsl(docColor);
			if (isNaN(hslColor.h)) {
				hslColor.h = 0; // process NaN hue values
			}

			return {
				id: doc.contentId,
				fullDoc: doc,
				src: doc.sourceId,
				srcName: doc.domain,
				reach: doc.reach,
				keywords: getAnnotations(doc.clusterFeatures),
				cluster: d + '',
				color: docColor,
				// if the original color is already desaturated (gray color shades), use default update color; otherwise, desaturate the original color
				updateColor: hslColor.s === 0 ? updateColor : d3.hsl(hslColor.h, 0, hslColor.l) // add desaturated update color to the data
			};
		});

		function nodes2groups(_nodes) {
			// group nodes by cluster index
			var _groups = d3
				.nest()
				.key(d => d.cluster)
				.entries(_nodes);

			return _.map(_groups, function(cluster) {
				cluster.keywords = [];
				var labels = getClusterLabels(cluster.values);
				if (labels && labels[0]) {
					cluster.keywords = labels[0].keywords;
				}

				cluster.totalDocumentCount = cluster.values.reduce(
					(acc, x) =>
						acc + x.fullDoc.topic.documentCount
							? x.fullDoc.topic.documentCount
							: 0,
					0
				);

				return cluster;
			});
		}

		var groups = nodes2groups(nodes);

		// reduce groups
		var diffCount = 3;
		if (groups.length < 50) {
			diffCount = 2;
		}
		if (groups.length < 30) {
			diffCount = 1;
		}
		if (groups.length < 10) {
			diffCount = 0;
		}

		var updatedNodes = [];
		_.each(groups, function(group) {
			var addUnChanged = true;
			var groupKeywords = _.map(group.keywords, 'name');
			groupKeywords.sort();

			_.each(groups, function(cGroup) {
				if (groups.key !== cGroup.key) {
					var cGroupKeywords = _.map(cGroup.keywords, 'name');
					cGroupKeywords.sort();

					if (_.difference(groupKeywords, cGroupKeywords).length < diffCount) {
						if (addUnChanged) {
							_.each(group.values, function(node) {
								node.cluster = cGroup.values[0].cluster;
								updatedNodes.push(node);
							});
						}
						addUnChanged = false;
					}
				}
			});

			if (addUnChanged) {
				_.each(group.values, function(node) {
					updatedNodes.push(node);
				});
			}
		});

		// group nodes by cluster index
		groups = nodes2groups(nodes);
		var maxGroups = interpolator.maxGroups();

		groups.sort((a, b) => {
			if (a.values.length < b.values.length || a.totalDocumentCount < b.totalDocumentCount) {
				return 1;
			}
			if (a.values.length > b.values.length || a.totalDocumentCount > b.totalDocumentCount) {
				return -1;
			}
			return 0;
		});

		if (groups.length > maxGroups) {
			groups = groups.slice(0, maxGroups);
		}
		minValue = 1;
		maxValue = 0;
		_.each(groups, function(g) {
			_.each(g.values, function(n) {
				if (n.reach < minValue) {
					minValue = n.reach;
				}
				if (n.reach > maxValue) {
					maxValue = n.reach;
				}
			});
		});

		size
			.domain([minValue, maxValue])
			.range([minValue === maxValue ? maxR : minR, maxR]);

		groups.forEach(g => {
			g.clusterMaxRadius = 0;
			g.values.forEach(n => {
				const nn = Object.assign(n, {
					radius: size(n.reach),
					originalRadius: size(n.reach)
				});

				const cstroke = nn.radius * interpolator.clusterStrokeFactor(maxR);
				if (cstroke > g.clusterMaxRadius) {
					g.clusterMaxRadius = cstroke;
				}

				return nn;
			});
		});

		chartData.groups = groups;

		//if colorMode is sentiment do initial replacement by cluster, otherwise by colors (topics)
		if (colorMode === 'sentiment') {
			_.each(chartData.groups, function(group, i) {
				_.each(group.values, function(node) {
					node.x =
						Math.cos(i / chartData.groups.length * 2 * Math.PI) * 200 +
						_width / 2 +
						Math.random() +
						10;
					node.y =
						Math.sin(i / chartData.groups.length * 2 * Math.PI) * 200 +
						_height / 2 +
						Math.random() +
						10;
				});
			});
		} else {
			var colors = d3
				.nest()
				.key(d => d.color)
				.entries(nodes);

			colors.forEach(function(x, i) {
				x.values.forEach(function(node) {
					node.x =
						Math.cos(i / colors.length * 2 * Math.PI) * 200 +
						_width / 2 +
						Math.random() +
						10;
					node.y =
						Math.sin(i / colors.length * 2 * Math.PI) * 200 +
						_height / 2 +
						Math.random() +
						10;
				});
			});
		}

		if (_debug) {
			// eslint-disable-next-line no-console
			console.info(
				'Clustering done in %i milliseconds',
				new Date() - startTime
			);
		}

		d3
			.select(_wrapper)
			.datum(chartData)
			.call(chart);

		return chart;
	};

	// chainable getter/setter methods
	chart.wrapper = function(_) {
		if (!arguments.length) return _wrapper;
		_wrapper = _;
		return chart;
	};

	chart.resize = function() {
		fitToWrapperBoundaries();
		chart.update();
	};

	chart.width = function(_) {
		if (!arguments.length) return _width;
		_width = _;
		if (!_customForceX) _forceX = interpolator.forceX();
		if (!_customForceY) _forceY = interpolator.forceY();
		return chart;
	};

	chart.height = function(_) {
		if (!arguments.length) return _height;
		_height = _;
		if (!_customForceX) _forceX = interpolator.forceX();
		if (!_customForceY) _forceY = interpolator.forceY();
		return chart;
	};

	chart.data = function(_) {
		if (!arguments.length) return _data;
		_data = _;
		updating = false;
		return chart;
	};

	chart.fontSizeAmplifier = function(_) {
		if (!arguments.length && isNaN(_)) return _fontSizeAmplifier;
		if (_ < 0.1) _fontSizeAmplifier = 0.1;
		if (_ > 10) _fontSizeAmplifier = 10;
		_fontSizeAmplifier = _;
		return chart;
	};

	chart.ES1Data = function(_) {
		if (!arguments.length) return _isES1Data;
		_isES1Data = _;
		return chart;
	};

	chart.keywordFilter = function(_) {
		if (!arguments.length) return _keywordFilter;
		_keywordFilter = _;
		return chart;
	};

	chart.clusters = function(_) {
		if (!arguments.length) return _clustersCount;
		_clustersCount = _;
		return chart;
	};

	chart.stop = function() {
		force.stop();
	};

	chart.colorMode = function(_) {
		if (!arguments.length) return colorMode;
		colorMode = _;
		return chart;
	};

	chart.colorScale = function(_) {
		if (!arguments.length) return colorScale;

		if (typeof _ !== 'function') {
			throw 'colorScale needs to be a function.';
		}

		colorScale = _;
		return chart;
	};

	chart.clustering = function(_) {
		if (!arguments.length) return _clustering;

		if (['louvain', 'kmeans', 'hierarchical'].indexOf(_) == -1) {
			throw "'" + _ + "' not supported as clustering algorithm";
		}

		_clustering = _;
		return chart;
	};

	chart.getLouvainClusters = function(data) {
		var node_data = [];
		var edge_data = [];

		_.each(data, function(doc) {
			// content id node
			node_data.push(doc.contentId);

			var filteredKeywords = [];

			_.each(doc.clusterFeatures, function(annotation) {
				var addRawKeyword = true;
				_.each(doc.clusterFeatures, function(checkAnnotation) {
					var tokens = checkAnnotation.name
						.toLowerCase()
						.replace('-', ' ')
						.split(' ');

					_.each(tokens, function(token) {
						if (annotation.name === token) {
							filteredKeywords.push(checkAnnotation.name.toLowerCase());
							addRawKeyword = false;
						}
					});
				});

				if (addRawKeyword) {
					filteredKeywords.push(annotation.name);
				}
			});

			filteredKeywords = _.uniq(filteredKeywords);

			_.each(filteredKeywords, function(keyword) {
				node_data.push(keyword);
				edge_data.push({
					source: doc.contentId,
					target: keyword
				});
			});
		});

		node_data = _.uniq(node_data);
		var community = jLouvain()
			.nodes(node_data)
			.edges(edge_data);
		var result = community();
		var assignments = _.map(data, doc => result[doc.contentId]);

		// var centroids =
		return {
			assignments: assignments,
			centroids: []
		};
	};

	chart.getTermMatrix = function(h) {
		// an array, where we'll list all the keywords
		var k = [];

		// the following code is quick and dirty and not optimal
		// .map is not ideal in that situation, and the .map and .reduce could
		// be combined into one single loop

		// h.map iterates through the documents and adds each
		// documentKeyword to k
		h.map(function(d) {
			d.filteredKeywords =
				typeof _keywordFilter === 'string' || Array.isArray(_keywordFilter)
					? filterAnnotations(d.clusterFeatures)
					: d.clusterFeatures;

			d.filteredKeywords.map(function(d) {
				k.push(d.name);
			});
		});

		// reduce the keyword list in k to a distinct list
		// containing every keyword only once
		var distinct = k.reduce(function(p, c) {
			if (p.indexOf(c) < 0) {
				p.push(c);
			}
			return p;
		}, []);

		// we're now again iterating over every document
		// and creating the term matrix for the keywords.
		// so what we have is a list of keywords like [a,b,c,...]
		// and are creating an array for every document which says if
		// the keyword is contained within the document like [0,1,1,...]
		// this way we get a vector map we can use for the clustering algorithm
		return h.map(function(d) {
			var keywords = d.filteredKeywords.map(d => d.name);

			return distinct.map(d => keywords.indexOf(d) < 0 ? 0 : 1);
		});
	};

	chart.getKMeansClusters = function(data, c) {
		var vectors = chart.getTermMatrix(data);
		var clusters = figue.kmeans(c, vectors);

		if (!clusters) {
			clusters = [];
		}
		return clusters;
	};

	chart.pickBestKMeansCluster = function(data, c) {
		// in previous k-means versions we were running the clustering only once
		// (there were other calls to the clustering if certain clusters contained
		// not enough documents, but I experienced infinite loops when using it with the keyword clustering)

		// for the following approach, see Wikipedia
		// ("Variations of k-means often include such optimizations as choosing the best of multiple runs",
		// http://en.wikipedia.org/wiki/Cluster_analysis)

		// the following approach runs the clustering multiple times (runs)
		// to be able to compare the quality of the clustering we have to come up with a measuring to do so
		// this quick'n'dirty solution sums up the average distances from each keyword to its cluster centroid
		// so in the end a lower number means a better clustering result

		// at the end we return the cluster with the lowest score only

		// the result is that we get much more consistent results for k-means!
		// (but still not deterministic)

		var sums = [];
		var clusters = [];
		var runs = 20;

		for (var i = 1; i <= runs; i++) {
			var cluster = chart.getKMeansClusters(data, c);

			if (cluster && cluster.centroids) {
				var sm = cluster.centroids.map(function(d) {
					var sum = d.reduce((a, b) => a + b, 0);

					var count = d.reduce((a, b) => b > 0 ? a + 1 : a, 0);

					return sum / count;
				});

				var sum = sm.reduce((a, b) => a + b, 0);

				sums.push(sum);
				clusters.push(cluster);
			}
		}

		var minKey;
		var minValue = Number.MAX_VALUE;

		for (var k = 0; k < sums.length; k++) {
			var v = sums[k];
			if (v <= minValue) {
				minKey = k;
				minValue = v;
			}
		}

		return clusters[minKey];
	};

	chart.getHierarchicalClusters = function(data) {
		var vectors = chart.getTermMatrix(data);

		var labels = [];
		for (var i = 0; i < data.length; i++) {
			labels[i] = data[i]['contentId'];
		}

		//perform clustering
		var root = figue.agglomerate(
			labels,
			vectors,
			figue.EUCLIDIAN_DISTANCE,
			figue.SINGLE_LINKAGE
		);

		if (!root) {
			return [];
		}

		// we calculate the mean distance between all leafes
		// we can use this value to trigger the creation of new clusters
		// so that we don't have to create new clusters on each step like before
		var getDistances = function(node, distances) {
			if (
				node.left !== null &&
				node.left.label > -1 &&
				node.right !== null &&
				node.right.label > -1
			) {
				distances.push(node.dist);
			}

			if (node.left !== null && node.left.label < 0) {
				distances = getDistances(node.left, distances);
			}
			if (node.right !== null && node.right.label < 0) {
				distances = getDistances(node.right, distances);
			}

			return distances;
		};

		var getNodes = function(side, node, parent, cluster, clusters, nodes) {
			node.bucket = cluster;
			if (parent !== null) {
				node.parent = parent;
			}

			var bucket = clusters;

			if (node.label > -1) {
				nodes.push(node);
			}

			var distances = getDistances(node, []);
			var meanDistance = distances[Math.round(distances.length / 2) - 1];

			if (node.left !== null) {
				if (node.left.label > -1) {
					var left = node.left;
					left.bucket = cluster;
					left.parent = node;
					nodes.push(left);
				} else {
					if (meanDistance * (node.left.size / 8) > node.left.dist) {
						bucket++;
					}
					data = getNodes('left', node.left, node, cluster, bucket, nodes);
					bucket = data.clusters;
				}
			}

			if (bucket !== clusters) {
				bucket++;
			}

			if (node.right !== null) {
				if (node.right.label > -1) {
					var right = node.right;
					right.bucket = cluster;
					right.parent = node;
					nodes.push(right);
				} else {
					if (meanDistance * (node.right.size / 8) > node.right.dist) {
						bucket++;
					}
					data = getNodes('right', node.right, node, bucket, bucket, nodes);
					bucket = data.clusters;
				}
			}

			return {
				nodes: nodes,
				clusters: bucket
			};
		};

		var map = getNodes(null, root, null, 0, 0, []);
		var nodes = map.nodes;

		var buckets = {};
		var clusters = {};

		clusters.assignments = [];

		for (var k = 0; k < nodes.length; k++) {
			var n = nodes[k];
			if (n.label > -1) {
				if (!buckets[n.bucket]) {
					buckets[n.bucket] = [];
				}

				buckets[n.bucket].push({
					label: n.label,
					id: k
				});

				clusters.assignments[k] = parseInt(n.bucket);
			}
		}

		clusters.centroids = _.map(buckets, (d, i) => parseInt(i));

		return clusters;
	};

	chart.highlightDocuments = function(documents) {
		if (documents !== null) {
			var clusterDocs = _.filter(_data.nodes, function(node) {
				return _.find(documents, {
					contentId: node.id
				});
			});

			if (clusterDocs.length > 0) {
				updateClusters(_.uniq(_.map(clusterDocs, 'cluster')));
				highlightNodes(_.map(clusterDocs, 'id'));
			}
		} else {
			// no document hovered or document not in list - reset highlight
			updateClusters();
			highlightNodes();
		}
	};

	chart.updatingParameters = function(color, duration) {
		if (!arguments.length) return [updateColor, updateDuration];
		updateColor = color;
		updateDuration = duration;
		return chart;
	};

	chart.greyOutElements = function() {
		updating = true;

		d3
			.selectAll('.clusterMapNodes')
			.selectAll('circle')
			.transition('clusterNodesGreyOut')
			.duration(updateDuration)
			.attr('stroke', d => d.updateColor)
			.attr('fill', d => d.updateColor);

		if (legend.initialized) {
			legend.hideValues();
		}

		return chart;
	};

	chart.setXForce = function(x) {
		if (!x) {
			_forceX = interpolator.forceX();
			_customForceX = false;
		} else {
			_forceX = x;
			_customForceX = true;
		}
		return chart;
	};

	chart.setYForce = function(y) {
		if (!y) {
			_forceY = interpolator.forceY();
			_customForceY = false;
		} else {
			_forceY = y;
			_customForceY = true;
		}
		return chart;
	};

	chart.showBubbleLegend = function(_) {
		if (!arguments.length || typeof _ !== 'boolean') return _showBubbleLegend;
		_showBubbleLegend = _;
		return chart;
	};

	chart.reheat = function() {
		if (force.alpha() <= 0.04) {
			force.alpha(0.045).restart();
		}
	};

	return chart;
};
